<template>
  <transition name="gh-sidebar-fade">
    <div v-if="isOpen" class="gh-sidebar-root">
      <aside
        class="gh-sidebar"
        :class="{
          'gh-sidebar--collapsed': !isExpanded,
          'gh-sidebar--resizing': isResizing,
        }"
        :style="sidebarStyle"
        role="dialog"
        aria-modal="false"
        :aria-label="$t('generation_history.history')"
        @click.stop
      >
        <div
          v-if="isExpanded"
          class="gh-sidebar__resize-handle"
          role="separator"
          @mousedown.prevent="startResize"
        ></div>
        <button type="button" class="gh-sidebar__expand-btn" @click="toggleExpanded">
          <i :class="isExpanded ? 'fa fa-chevron-right' : 'fa fa-chevron-left'" aria-hidden="true"></i>
        </button>

        <div class="gh-sidebar__inner">
          <template v-if="view === 'list'">
            <header class="gh-sidebar__header">
              <div class="gh-sidebar__tabs">
                <button
                  type="button"
                  class="gh-sidebar__tab"
                  :class="{ 'is-active': activeTab === 'history' }"
                  @click="setActiveTab('history')"
                >
                  {{ $t('generation_history.history') }}
                </button>
                <button
                  type="button"
                  class="gh-sidebar__tab"
                  :class="{ 'is-active': activeTab === 'favorites' }"
                  @click="setActiveTab('favorites')"
                >
                  {{ $t('generation_history.favorites_list') }}
                </button>
              </div>
              <button type="button" class="gh-sidebar__close" @click="closeSidebar">
                <span>×</span>
              </button>
            </header>
            <GenerationHistoryFavoritesListView v-if="activeTab === 'favorites'" />
            <GenerationHistoryListView v-else />
          </template>

          <GenerationHistoryDetailView v-else />
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import GenerationHistoryDetailView from './detail/GenerationHistoryDetailView.vue';
import GenerationHistoryFavoritesListView from './list/GenerationHistoryFavoritesListView.vue';
import GenerationHistoryListView from './list/GenerationHistoryListView.vue';

const STORAGE_KEY = 'generationHistorySidebarWidthPercent';
const MIN_WIDTH_PERCENT = 20;
const MAX_WIDTH_PERCENT = 90;
const DEFAULT_WIDTH_PERCENT = 48;

function clampWidthPercent(value) {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) {
    return DEFAULT_WIDTH_PERCENT;
  }
  return Math.min(MAX_WIDTH_PERCENT, Math.max(MIN_WIDTH_PERCENT, parsed));
}

function loadStoredWidthPercent() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      return DEFAULT_WIDTH_PERCENT;
    }
    return clampWidthPercent(stored);
  } catch (error) {
    return DEFAULT_WIDTH_PERCENT;
  }
}

function saveStoredWidthPercent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, String(clampWidthPercent(value)));
  } catch (error) {
    // Ignore storage errors (private mode, quota exceeded, etc.)
  }
}

export default {
  name: 'GenerationHistorySidebar',
  components: {
    GenerationHistoryListView,
    GenerationHistoryFavoritesListView,
    GenerationHistoryDetailView,
  },
  data() {
    return {
      sidebarWidthPercent: loadStoredWidthPercent(),
      isResizing: false,
    };
  },
  computed: {
    ...mapState('generationHistory', [
      'isOpen',
      'isExpanded',
      'activeTab',
      'view',
    ]),
    sidebarStyle() {
      if (!this.isExpanded) {
        return null;
      }
      return {
        width: `${this.sidebarWidthPercent}vw`,
      };
    },
  },
  beforeDestroy() {
    this.stopResize();
  },
  methods: {
    ...mapActions('generationHistory', [
      'closeSidebar',
      'toggleExpanded',
      'setActiveTab',
    ]),
    startResize(event) {
      if (!this.isExpanded || event.button !== 0) {
        return;
      }
      this.isResizing = true;
      document.body.classList.add('gh-sidebar-resize-active');
      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.stopResize);
    },
    onResizeMove(event) {
      if (!this.isResizing) {
        return;
      }
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      if (!viewportWidth) {
        return;
      }
      const nextWidthPercent = ((viewportWidth - event.clientX) / viewportWidth) * 100;
      this.sidebarWidthPercent = clampWidthPercent(nextWidthPercent);
    },
    stopResize() {
      if (!this.isResizing) {
        return;
      }
      this.isResizing = false;
      document.body.classList.remove('gh-sidebar-resize-active');
      document.removeEventListener('mousemove', this.onResizeMove);
      document.removeEventListener('mouseup', this.stopResize);
      saveStoredWidthPercent(this.sidebarWidthPercent);
    },
  },
};
</script>

<style lang="scss">
@import './shared/_generation-history.scss';
</style>
